import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ClipboardCheck, Calendar, CalendarClock } from 'lucide-react'

import {
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import {
  PageWrapper,
  DetailPageLayout,
  ContentCard,
} from '@/components/newRedesign'
import {
  ClearFiltersButton,
  DataTableNew,
  DateRangeFilter,
  generateSkeletonData,
  isSkeletonRow,
  ListPageHeader,
  PageSizeFilter,
  SearchFilter,
  useSkeletonColumns,
} from '@/components/newRedesign/components'
import { SimpleTablePagination } from '@/components/table/SimpleTablePagination'
import { lotesColumns } from './Columns'
import { LotesTableContextProvider } from './TableContext'
import { useProductDetailsContext } from '../contexts/ProductDetailsContext'
import { useLotes } from './hooks/useLotes'
import MovimientosModal from './MovimientosModal'
import { LotesAdvancedFilters } from './filters/LotesAdvancedFilters'
import { Lote } from '../interfaces/inventory'

import '../styles/inventory.css'

// Componente interno que usa el hook de lotes
function LotesViewContent() {
  const { inventoryId } = useParams()
  const navigate = useNavigate()
  const { setSelectedProductId, productMainInfo } = useProductDetailsContext()

  const [inventoryName, setInventoryName] = useState('')
  const [showMovimientosModal, setShowMovimientosModal] = useState(false)
  const [selectedLote, setSelectedLote] = useState<{
    id: string
    name: string
  } | null>(null)

  // Auto-select product based on inventoryId from URL
  useEffect(() => {
    if (inventoryId && setSelectedProductId) {
      const productId = parseInt(inventoryId)
      if (!isNaN(productId)) {
        setSelectedProductId(productId)
      }
    }
  }, [inventoryId, setSelectedProductId])

  useEffect(() => {
    if (productMainInfo?.productName) {
      setInventoryName(productMainInfo.productName)
    }
  }, [productMainInfo?.productName])

  const handleInventoryNavigation = () => {
    navigate('/logistic/inventory')
  }

  const handleRowClick = (loteId: string, loteName: string) => {
    setSelectedLote({ id: loteId, name: loteName })
    setShowMovimientosModal(true)
  }

  const handleCloseModal = () => {
    setShowMovimientosModal(false)
    setSelectedLote(null)
  }

  return (
    <PageWrapper>
      {/* Header con Breadcrumb */}
      <PageWrapper.Header
        breadcrumbIcon={<ClipboardCheck className="h-4 w-4 text-gray-400" />}
        breadcrumbItems={[
          { label: 'Logística' },
          {
            label: 'Inventario',
            onClick: handleInventoryNavigation,
          },
          { label: inventoryName },
        ]}
        className="-mb-0"
        titleSection={{
          title: inventoryName,
        }}
      />

      <DetailPageLayout.Content className="mt-2 flex-1 md:overflow-hidden">
        <DetailPageLayout.Main className="flex h-full flex-col md:overflow-hidden">
          {/* Contenedor de Lotes como ContentCard */}
          <ContentCard className="flex h-auto min-h-0 flex-1 flex-col md:h-full md:overflow-hidden">
            <ContentCard.Header className="-mb-0 flex-shrink-0" title="Lotes" />
            <div className="-mb-4 flex min-h-0 flex-1 flex-col md:overflow-hidden">
              {/* Header with filters */}
              <div className="flex-shrink-0">
                <LotesListHeaderWrapper />
              </div>

              {/* Table - Using flex-1 to occupy available space */}
              <div className="mt-4 flex flex-1 flex-col md:overflow-hidden">
                <LotesTable onRowClick={handleRowClick} />
              </div>
            </div>
          </ContentCard>
        </DetailPageLayout.Main>
      </DetailPageLayout.Content>

      {/* Movimientos Modal */}
      <MovimientosModal
        isOpen={showMovimientosModal}
        loteId={selectedLote?.id || ''}
        loteName={selectedLote?.name || ''}
        onClose={handleCloseModal}
      />
    </PageWrapper>
  )
}

// Wrapper component that has access to useLotes hook
function LotesListHeaderWrapper() {
  const { pageSize, setPageSize, filters, setFilters } = useLotes()

  // Extraer valores de filtros para la UI
  const searchValue = filters.searchValue || ''
  const entryDateFrom = filters.entryDateFrom
  const entryDateTo = filters.entryDateTo
  const expiryDateFrom = filters.expiryDateFrom
  const expiryDateTo = filters.expiryDateTo

  // Funciones para actualizar filtros
  const setSearchValue = (value: string) => {
    setFilters((prev) => ({ ...prev, searchValue: value }))
  }

  // Date filter handlers for entry date
  const handleEntryDateFromChange = (date: string) => {
    setFilters((prev) => ({ ...prev, entryDateFrom: date || undefined }))
  }

  const handleEntryDateToChange = (date: string) => {
    setFilters((prev) => ({ ...prev, entryDateTo: date || undefined }))
  }

  // Date filter handlers for expiry date
  const handleExpiryDateFromChange = (date: string) => {
    setFilters((prev) => ({
      ...prev,
      expiryDateFrom: date || undefined,
    }))
  }

  const handleExpiryDateToChange = (date: string) => {
    setFilters((prev) => ({ ...prev, expiryDateTo: date || undefined }))
  }

  // Handle advanced filters change
  const handleAdvancedFiltersChange = (advancedFilters: {
    cantidadMin?: string
    cantidadMax?: string
    costoCompraMin?: string
    costoCompraMax?: string
    costoVentaMin?: string
    costoVentaMax?: string
    proveedorId?: string
    ubicacion?: string
    contenedorId?: number
  }) => {
    setFilters((prev) => ({
      ...prev,
      ...advancedFilters,
    }))
  }

  const clearAllFilters = () => {
    setFilters({
      searchValue: '',
      entryDateFrom: undefined,
      entryDateTo: undefined,
      expiryDateFrom: undefined,
      expiryDateTo: undefined,
      cantidadMin: undefined,
      cantidadMax: undefined,
      costoCompraMin: undefined,
      costoCompraMax: undefined,
      costoVentaMin: undefined,
      costoVentaMax: undefined,
      proveedorId: undefined,
      ubicacion: undefined,
      contenedorId: undefined,
    })
  }

  // Check if there are active filters
  const hasActiveFilters =
    searchValue.trim() !== '' ||
    !!entryDateFrom ||
    !!entryDateTo ||
    !!expiryDateFrom ||
    !!expiryDateTo ||
    !!filters.cantidadMin ||
    !!filters.cantidadMax ||
    !!filters.costoCompraMin ||
    !!filters.costoCompraMax ||
    !!filters.costoVentaMin ||
    !!filters.costoVentaMax ||
    !!filters.proveedorId ||
    !!filters.ubicacion ||
    !!filters.contenedorId

  return (
    <LotesListHeader
      advancedFilters={{
        cantidadMin: filters.cantidadMin,
        cantidadMax: filters.cantidadMax,
        costoCompraMin: filters.costoCompraMin,
        costoCompraMax: filters.costoCompraMax,
        costoVentaMin: filters.costoVentaMin,
        costoVentaMax: filters.costoVentaMax,
        proveedorId: filters.proveedorId,
        ubicacion: filters.ubicacion,
        contenedorId: filters.contenedorId,
      }}
      clearAllFilters={clearAllFilters}
      entryDateFrom={entryDateFrom}
      entryDateTo={entryDateTo}
      expiryDateFrom={expiryDateFrom}
      expiryDateTo={expiryDateTo}
      handleAdvancedFiltersChange={handleAdvancedFiltersChange}
      handleEntryDateFromChange={handleEntryDateFromChange}
      handleEntryDateToChange={handleEntryDateToChange}
      handleExpiryDateFromChange={handleExpiryDateFromChange}
      handleExpiryDateToChange={handleExpiryDateToChange}
      hasActiveFilters={hasActiveFilters}
      pageSize={pageSize}
      searchValue={searchValue}
      setPageSize={setPageSize}
      setSearchValue={setSearchValue}
    />
  )
}

// Componente para el header con filtros
interface LotesListHeaderProps {
  searchValue: string
  setSearchValue: (_value: string) => void
  entryDateFrom: string | undefined
  entryDateTo: string | undefined
  expiryDateFrom: string | undefined
  expiryDateTo: string | undefined
  handleEntryDateFromChange: (_date: string) => void
  handleEntryDateToChange: (_date: string) => void
  handleExpiryDateFromChange: (_date: string) => void
  handleExpiryDateToChange: (_date: string) => void
  pageSize: number
  setPageSize: (_size: number) => void
  advancedFilters: {
    cantidadMin?: string
    cantidadMax?: string
    costoCompraMin?: string
    costoCompraMax?: string
    costoVentaMin?: string
    costoVentaMax?: string
    proveedorId?: string
    ubicacion?: string
    contenedorId?: number
  }
  handleAdvancedFiltersChange: (_filters: {
    cantidadMin?: string
    cantidadMax?: string
    costoCompraMin?: string
    costoCompraMax?: string
    costoVentaMin?: string
    costoVentaMax?: string
    proveedorId?: string
    ubicacion?: string
    contenedorId?: number
  }) => void
  clearAllFilters: () => void
  hasActiveFilters: boolean
}

function LotesListHeader({
  searchValue,
  setSearchValue,
  entryDateFrom,
  entryDateTo,
  expiryDateFrom,
  expiryDateTo,
  handleEntryDateFromChange,
  handleEntryDateToChange,
  handleExpiryDateFromChange,
  handleExpiryDateToChange,
  pageSize,
  setPageSize,
  advancedFilters,
  handleAdvancedFiltersChange,
  clearAllFilters,
  hasActiveFilters,
}: LotesListHeaderProps) {
  return (
    <ListPageHeader
      search={
        <SearchFilter
          onChange={setSearchValue}
          placeholder="Buscar Número de lote..."
          value={searchValue}
        />
      }
      secondaryActions={
        <>
          {/* Secondary buttons - Order 2 */}
          <div className="flex-none" style={{ order: 2 }}>
            <PageSizeFilter
              onChange={setPageSize}
              options={[10, 20, 30, 40, 50]}
              pageSize={pageSize}
            />
          </div>

          {/* Filters - Order 3 */}
          <div className="flex-none" style={{ order: 3 }}>
            <DateRangeFilter
              dateFrom={entryDateFrom || ''}
              dateTo={entryDateTo || ''}
              icon={<Calendar className="h-4 w-4" />}
              onDateFromChange={handleEntryDateFromChange}
              onDateToChange={handleEntryDateToChange}
              placeholder="Fecha de entrada"
            />
          </div>

          <div className="flex-none" style={{ order: 3 }}>
            <DateRangeFilter
              dateFrom={expiryDateFrom || ''}
              dateTo={expiryDateTo || ''}
              icon={<CalendarClock className="h-4 w-4" />}
              onDateFromChange={handleExpiryDateFromChange}
              onDateToChange={handleExpiryDateToChange}
              placeholder="Fecha de vencimiento"
            />
          </div>

          <div className="flex-none" style={{ order: 3 }}>
            <LotesAdvancedFilters
              filters={advancedFilters}
              onFiltersChange={handleAdvancedFiltersChange}
            />
          </div>

          {/* Clear All Filters Button - Order 4 (appears at the end) */}
          <div className="flex-none" style={{ order: 4 }}>
            <ClearFiltersButton
              onClick={clearAllFilters}
              visible={hasActiveFilters}
            />
          </div>
        </>
      }
    />
  )
}

// Componente para la tabla de lotes
function LotesTable({
  onRowClick,
}: {
  onRowClick: (_loteId: string, _loteName: string) => void
}) {
  const {
    lotes,
    isLoading,
    isFetching,
    currentPage,
    setCurrentPage,
    pageSize,
    totalItems,
    totalPages,
    refetch,
  } = useLotes()

  // Manage column visibility with localStorage
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = localStorage.getItem('lotes-table-columns')
      if (stored) {
        return JSON.parse(stored)
      }
      return {}
    }
  )

  const skeletonData = useMemo(() => {
    // Mostrar skeleton durante carga inicial O durante fetching
    if (!isLoading && !isFetching) return []
    return generateSkeletonData<Lote>(pageSize)
  }, [isLoading, isFetching, pageSize])

  // Usar skeleton data cuando está cargando O fetching
  const data = isLoading || isFetching ? skeletonData : lotes || []

  // Columns with skeleton during loading
  const tableColumns = useSkeletonColumns(lotesColumns, isLoading || isFetching)

  // Create table with TanStack
  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === 'function' ? updater(columnVisibility) : updater
      setColumnVisibility(newVisibility)
      localStorage.setItem('lotes-table-columns', JSON.stringify(newVisibility))
    },
    state: {
      columnVisibility,
    },
  })

  // Handle row click
  const handleRowClick = (item: Lote) => {
    // Don't navigate if skeleton or loading
    if (isLoading || isFetching || isSkeletonRow(item)) return

    onRowClick(item.id?.toString() || '', item.name || item.productName || '')
  }

  return (
    <div className="flex h-full max-h-[500px] flex-col md:max-h-none">
      <div className="relative min-h-[200px] flex-1 overflow-hidden md:min-h-0">
        {isFetching && !isLoading && (
          <div className="absolute left-0 right-0 top-0 z-10 h-0.5 overflow-hidden bg-gray-100">
            <div className="h-full w-full animate-pulse bg-blue-500" />
          </div>
        )}
        <DataTableNew height="100%" onRowClick={handleRowClick} table={table} />
      </div>

      {/* Paginación - altura fija */}
      <div className="flex-shrink-0">
        <SimpleTablePagination
          currentPage={currentPage}
          isLoading={isLoading || isFetching}
          onPageChange={setCurrentPage}
          onRefresh={refetch}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
        />
      </div>
    </div>
  )
}

// Componente principal que envuelve todo con el provider
export default function LotesView() {
  const { inventoryId } = useParams()

  return (
    <LotesTableContextProvider inventoryId={inventoryId || ''}>
      <LotesViewContent />
    </LotesTableContextProvider>
  )
}
